import { API } from "@gui/infrastructure/pass-through/api";
import { GEO_VALIDATOR_SOURCE, STATUS_CALCULATOR_SOURCE, type Job } from "@domain/CRM/job";
import type { JobCardAgeFrom } from "./components/JobCard";
import type { JobDetailTabActionKind } from "@gui/widgets/JobDetail/JobDetailTabAction";

export type JobErrorTabConfig = {
	label: string;
	/** The error source the job cards list on this tab; null lists every error with its source. */
	errorSource: string | null;
	/** The date the job cards' age chip counts from — the one this tab's query windows on. */
	ageFrom: JobCardAgeFrom;
	/** Shown (and fetched) only for developers. */
	developerOnly?: boolean;
	fetch: () => Promise<Job[]>;
	/** A job action this tab offers in the job detail's toolbar. Its request resolves once the work is done, after
	 *  which the page refetches (the job usually leaves the tab). */
	action?: {
		kind: JobDetailTabActionKind;
		failure: string;
		/** The ways to run it; several make the toolbar button a menu. */
		choices: JobErrorTabActionChoice[];
	};
	/** An action offered only for a multi-job selection, where the tab has no single-job `action` of its own. */
	batchOnlyAction?: JobErrorBatchAction;
};

/** One way to run a tab's action on a job. */
type JobErrorTabActionChoice = { label: string; perform: (jobId: string) => Promise<unknown> };

/** What a batch over the selected jobs can run: the tab's action minus its toolbar presentation. */
export type JobErrorBatchAction = { failure: string; choices: JobErrorTabActionChoice[] };

const EXIT_INTERVIEW_SOURCE = "Exit Interview Lambda";

/** The subsets of job errors the page can show, each with its own endpoint (the Exe tab took over from the retired
 *  Exe Mission Validation page). */
export const JOB_ERROR_TABS = {
	geo: {
		label: "Geo Validation",
		errorSource: GEO_VALIDATOR_SOURCE,
		ageFrom: "dateCreated",
		fetch: () => API.Data.Jobs.issues.getGeoValidationJobs(),
		action: {
			kind: "processGeo",
			failure: "Failed to re-process the geo data",
			choices: [
				{ label: "Re-run validation", perform: (jobId) => API.Data.Jobs.issues.processGeo(jobId) },
				// Processes the boundary without validating, which clears the job's Geo-Spatial Validator errors.
				{
					label: "Skip validation",
					perform: (jobId) => API.Data.Jobs.issues.processGeo(jobId, { skipValidation: true }),
				},
			],
		},
	},
	manualReview: {
		label: "Manual Review",
		errorSource: EXIT_INTERVIEW_SOURCE,
		ageFrom: "dateSampled",
		fetch: () => API.Data.Jobs.issues.getManualReviewJobs(),
		// Flags the job approved, then re-runs the exit interview, which lifts its manual-review hold.
		action: {
			kind: "approveManualReview",
			failure: "Failed to approve the manual review",
			choices: [{ label: "Approve", perform: (jobId) => API.Data.Jobs.issues.approveManualReview(jobId) }],
		},
	},
	exe: {
		label: "Exe Validation",
		errorSource: EXIT_INTERVIEW_SOURCE,
		ageFrom: "dateSampled",
		fetch: () => API.Data.Jobs.issues.getExeMissionValidationJobs(),
		// The single job's re-run is the panel header's own menu (JobDetail's `reprocess`); only a batch needs it here.
		batchOnlyAction: {
			failure: "Failed to re-run file generation",
			choices: [
				{ label: "Normal", perform: (jobId) => API.Data.Jobs.issues.fixExeMissionValidationJobs([jobId], false) },
				{
					label: "Ignore validation",
					perform: (jobId) => API.Data.Jobs.issues.fixExeMissionValidationJobs([jobId], true),
				},
			],
		},
	},
	status: {
		label: "Status",
		developerOnly: true,
		errorSource: STATUS_CALCULATOR_SOURCE,
		ageFrom: "dateCreated",
		fetch: () => API.Data.Jobs.issues.getStatusIssueJobs(),
	},
	misc: {
		label: "Misc",
		// Whatever the other tabs don't cover, so there's no one source to pick.
		errorSource: null,
		ageFrom: "dateCreated",
		fetch: () => API.Data.Jobs.issues.getMiscIssueJobs(),
	},
} satisfies Record<string, JobErrorTabConfig>;

export type JobErrorTab = keyof typeof JOB_ERROR_TABS;

const TAB_KEYS = Object.keys(JOB_ERROR_TABS) as JobErrorTab[];
const TAB_CONFIGS: Record<JobErrorTab, JobErrorTabConfig> = JOB_ERROR_TABS;

/** The tabs a user may see, in order: developer-only tabs are hidden from other admins. */
export function visibleJobErrorTabs(isDeveloper: boolean): JobErrorTab[] {
	return TAB_KEYS.filter((key) => isDeveloper || !TAB_CONFIGS[key].developerOnly);
}

/** The action a batch over the tab's selected jobs runs, if the tab has one. */
export function batchActionOf(tab: JobErrorTab): JobErrorBatchAction | undefined {
	return TAB_CONFIGS[tab].action ?? TAB_CONFIGS[tab].batchOnlyAction;
}

/** The jobs across the given tabs' lists, each once — a job can sit on more than one tab. */
export function uniqueJobErrorJobs(lists: readonly (readonly Job[])[]): Job[] {
	const byId = new Map<string, Job>();
	for (const list of lists) for (const job of list) byId.set(job.id, job);
	return [...byId.values()];
}

/** Every job the Job Errors page would list for this user — what its sidebar/Home badge counts, so the badge
 *  never shows jobs sitting on tabs the user can't open. */
export async function fetchVisibleJobErrorJobs(isDeveloper: boolean): Promise<Job[]> {
	const lists = await Promise.all(visibleJobErrorTabs(isDeveloper).map((key) => TAB_CONFIGS[key].fetch()));
	return uniqueJobErrorJobs(lists);
}
